import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
/**
 * Many products at once, the Trash, and deleting for good (B-80b, docs/specs/catalog.md Part 4). A product with
 * sales or stock history is never deleted: it is archived instead, and the answer says so.
 */
export const MAX_BULK_PRODUCTS = 100;
export const productIdsRequestSchema = z
    .object({ ids: z.array(z.uuid()).min(1).max(MAX_BULK_PRODUCTS) })
    .strict()
    .refine((value) => new Set(value.ids).size === value.ids.length, {
    message: "Each product once",
    path: ["ids"],
});
export const deleteProductsRequestSchema = z
    .object({
    ids: z.array(z.uuid()).min(1).max(MAX_BULK_PRODUCTS),
    /** Skip the Trash: delete for good at once. */
    permanent: z.boolean().default(false),
})
    .strict()
    .refine((value) => new Set(value.ids).size === value.ids.length, {
    message: "Each product once",
    path: ["ids"],
});
/** What happened to each product id of the request. */
export const productBinResultSchema = z.object({
    trashed: z.array(z.uuid()),
    deleted: z.array(z.uuid()),
    /** Had history, so archived instead of deleted. */
    archived: z.array(z.uuid()),
    restored: z.array(z.uuid()),
    /** Bulk archive / bring back: the products whose status changed. */
    changed: z.array(z.uuid()),
    /** Empty Trash: products still in the Trash after this batch (call again). */
    remaining: z.int(),
});
const catalog = { tag: "catalog" };
const manage = { permission: "catalog.manage" };
const bulk = (name, path, summary, extra = {}) => defineEndpoint({
    ...catalog,
    name,
    method: "POST",
    path: `/v1/products/${path}`,
    access: manage,
    summary,
    body: productIdsRequestSchema,
    status: 200,
    response: productBinResultSchema,
    ...extra,
});
export const deleteProducts = defineEndpoint({
    ...catalog,
    name: "deleteProducts",
    method: "POST",
    path: "/v1/products/delete",
    access: manage,
    summary: "Delete products: to the Trash (or for good with `permanent`); ones with history are archived",
    body: deleteProductsRequestSchema,
    status: 200,
    response: productBinResultSchema,
});
export const restoreProducts = bulk("restoreProducts", "restore", "Bring products back from the Trash", {
    errors: ["PLAN_LIMIT_REACHED"],
});
export const purgeProducts = bulk("purgeProducts", "purge", "Delete products in the Trash for good (ones that gained history are archived)");
export const archiveProducts = bulk("archiveProducts", "archive", "Archive many products");
export const unarchiveProducts = bulk("unarchiveProducts", "unarchive", "Bring many archived products back", {
    errors: ["PLAN_LIMIT_REACHED"],
});
export const emptyProductTrash = defineEndpoint({
    ...catalog,
    name: "emptyProductTrash",
    method: "POST",
    path: "/v1/products/empty-trash",
    access: manage,
    summary: "Delete everything in the Trash for good, up to 500 per call (`remaining` says if more are left)",
    body: z.object({}).strict(),
    status: 200,
    response: productBinResultSchema,
});
// Events ------------------------------------------------------------------------------------------------------
/** `catalog.product.deleted`: gone for good; search drops it, POS devices drop its variants. */
export const productDeletedEventSchema = z.object({ id: z.uuid(), variantIds: z.array(z.uuid()) });
//# sourceMappingURL=catalog-bin.js.map